import React, { useCallback, useState } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fg } from '@atlassian/jira-feature-gating';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useFieldConfig } from '@atlassian/jira-issue-field-base/src/services/field-config-service/main.tsx';
import type { FieldConfiguration } from '@atlassian/jira-issue-field-base/src/services/field-config-service/types.tsx';
import { useIsIssueOfIncidentsPractice } from '@atlassian/jira-issue-field-servicedesk-practices/src/services/use-practices-field-value/index.tsx';

import { useUpdatedServices } from '@atlassian/jira-jsm-stakeholder-updates/src/services/index.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { RelayMigrationProvider } from '@atlassian/jira-relay-apollo-migration-utils/src/services/use-query/index.tsx';
import { InsightObjectGraphModal } from '@atlassian/jira-servicedesk-insight-object-graph/src/ui/index.tsx';
import { AssetsObjectGraphModal } from '@atlassian/jira-assets-object-graph/src/ui/index.tsx';
import type { CmdbObject } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import {
	toString,
	type AutoCompleteConfigType,
	type ServiceDetails,
} from '../common/types/index.tsx';
import { CollapsibleField } from '../common/ui/collapsible-field/index.tsx';
import { useResolveFieldValuesByIdGraphQL } from '../services/resolve-field-values-by-id/index.tsx';
import { useAutoComplete } from '../services/use-auto-complete/index.tsx';
import { AdditionalLabelContent } from './additional-label-content/index.tsx';
import { DetailContent } from './detail-content/index.tsx';
import {
	HeaderContent as HeaderContentWithProjectKey,
	type Props as HeaderContentWithInjectedProps,
} from './header-content/index.tsx';
import messages from './messages.tsx';
import { OptionContent } from './option-content/index.tsx';
import type { ServiceEntityMainProps } from './types.tsx';

type ProjectKey = string;

type HeaderContentWithoutInjectedProps = Flow.Diff<
	HeaderContentWithInjectedProps,
	{
		projectKey: ProjectKey;
		onViewObjectDetails: (arg1: CmdbObject, arg2: UIAnalyticsEvent) => void;
	}
>;

export const ServiceEntityMain = (props: ServiceEntityMainProps): React.JSX.Element | null => {
	const isIssueOfIncidentPractice = useIsIssueOfIncidentsPractice();

	const [, actions] = useUpdatedServices();
	const { area, fieldId, projectKey, onChange, initialServices } = props;
	const issueKey = useIssueKey();

	const [currentOpenObject, setCurrentOpenObject] = useState<CmdbObject | undefined>();
	const [{ value: fieldConfig }] = useFieldConfig(issueKey, toString(fieldId));

	const onViewObjectDetails = useCallback((cmdbObject: CmdbObject) => {
		setCurrentOpenObject(cmdbObject);
	}, []);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onCloseObjectDialog = () => {
		setCurrentOpenObject(undefined);
	};

	const HeaderContentWithoutProjectKey = useCallback(
		(headerContentWithoutProjectKeyProps: HeaderContentWithoutInjectedProps) => (
			<HeaderContentWithProjectKey
				projectKey={projectKey}
				onViewObjectDetails={onViewObjectDetails}
				{...headerContentWithoutProjectKeyProps}
			/>
		),
		[onViewObjectDetails, projectKey],
	);

	const render = {
		HeaderContent: HeaderContentWithoutProjectKey,
		DetailContent,
		OptionContent,
		AdditionalLabelContent,
	};

	if (!fieldConfig) {
		return null;
	}

	const autoCompleteConfig: AutoCompleteConfigType<
		FieldConfiguration<undefined> | null,
		ServiceDetails
	> = {
		context: fieldConfig,
		// eslint-disable-next-line react-hooks/react-compiler
		useAutoComplete,
	};

	const onSuccess = () => {
		fireOperationalAnalytics(createAnalyticsEvent({}), 'affectedServicesField updateSucceeded', {});
	};
	const onFailure = () => {
		fireOperationalAnalytics(createAnalyticsEvent({}), 'affectedServicesField updateFailed', {});
	};
	return (
		<RelayMigrationProvider>
			<CollapsibleField
				initialServices={initialServices}
				isDuplicatesAllowed={false}
				fieldId={fieldId}
				actionSubject="serviceRegistryInlineEdit"
				source="service"
				isMultiple
				area={area}
				// @ts-expect-error - TS2322 - Type '{ HeaderContent: (headerContentWithoutProjectKeyProps: HeaderContentWithoutInjectedProps) => JSX.Element; DetailContent: (props: Props) => JSX.Element | null; OptionContent: (props: OptionRenderProps<...>) => JSX.Element; AdditionalLabelContent: ({ values }: AdditionalLabelRenderProps<...>) => JSX.Element; }' is not assignable to type 'RenderType<ServiceDetails>'.
				render={render}
				messages={messages}
				autoCompleteConfig={autoCompleteConfig}
				// eslint-disable-next-line react-hooks/react-compiler
				useResolveFieldValuesById={useResolveFieldValuesByIdGraphQL}
				onSuccess={onSuccess}
				onFailure={onFailure}
				onChange={onChange}
				onConfirm={() => {
					if (isIssueOfIncidentPractice) {
						actions.updateServices();
					}
				}}
			/>

			{fg('assets_embed_object_view_in_object_graph') ? (
				<AssetsObjectGraphModal
					objectId={currentOpenObject?.objectId}
					objectTypeId={currentOpenObject?.objectType.objectTypeId}
					onClose={onCloseObjectDialog}
					source="service"
					isObjectGraphOpen={!!currentOpenObject}
					disableResizePanel
				/>
			) : (
				<InsightObjectGraphModal
					objectId={currentOpenObject?.objectId}
					objectTypeId={currentOpenObject?.objectType.objectTypeId}
					onClose={onCloseObjectDialog}
					source="service"
					isObjectGraphOpen={!!currentOpenObject}
				/>
			)}
		</RelayMigrationProvider>
	);
};
